import { useEffect, useState } from 'react';

import { VIMEO, YOUTUBE, PERSONALWERK } from '@xing-com/crate-jobs-constants';
import { useViewerData } from '@xing-com/hub';

type Consent = {
  hasConsent: boolean;
};

type ThirdPartyContentConsentResult = {
  loading: boolean;
  hasConsent?: boolean;
  userCentrics: {
    isLoading: boolean;
    youtube?: Consent;
    vimeo?: Consent;
    personalwerk?: Consent;
  };
};

const getConsent = async (
  vendorIds: string[]
): Promise<Record<string, boolean>> => {
  const consentDetails = await window.__ucCmp?.getConsentDetails();

  return Object.fromEntries(
    vendorIds.map((vendorId) => [
      vendorId,
      consentDetails?.services?.[vendorId]?.consent?.given ?? false,
    ])
  );
};

export const useExternalContentConsent = (): ThirdPartyContentConsentResult => {
  const [consent, setConsent] = useState<{
    youtube: boolean;
    vimeo: boolean;
    personalwerk: boolean;
  }>({
    youtube: false,
    vimeo: false,
    personalwerk: false,
  });
  const [loadingConsent, setLoadingConsent] = useState(false);
  const viewerData = useViewerData();

  useEffect(() => {
    const updateConsent = async (): Promise<void> => {
      setLoadingConsent(true);
      try {
        const consent = await getConsent([YOUTUBE, VIMEO, PERSONALWERK]);
        setConsent({
          youtube: consent[YOUTUBE],
          vimeo: consent[VIMEO],
          personalwerk: consent[PERSONALWERK],
        });
      } catch {
        setLoadingConsent(false);
      }
      setLoadingConsent(false);
    };

    updateConsent();
    window.addEventListener('UC_UI_INITIALIZED', updateConsent);
    window.addEventListener('UC_CONSENT', updateConsent);
    window.addEventListener('UC_UI_CMP_EVENT', updateConsent);

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', updateConsent);
      window.removeEventListener('UC_CONSENT', updateConsent);
      window.removeEventListener('UC_UI_CMP_EVENT', updateConsent);
    };
  }, []);

  const isLoggedOut = viewerData.data?.loginState === 'LOGGED_OUT';
  const hasConsent =
    isLoggedOut || viewerData.data?.hasThirdPartyTrackingConsent;

  return {
    loading: viewerData.loading,
    hasConsent,
    userCentrics: {
      isLoading: loadingConsent,
      youtube: { hasConsent: consent.youtube },
      vimeo: { hasConsent: consent.vimeo },
      personalwerk: { hasConsent: consent.personalwerk },
    },
  };
};
